import React, { useState, useEffect, useCallback } from "react";
import PortalAdminLayout from "./PortalAdminLayout";

const SMSOptOutManagement = () => {
  const [optOuts, setOptOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination & Filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [stats, setStats] = useState({});

  // Add/Remove modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [newReason, setNewReason] = useState("admin action");
  const [newNotes, setNewNotes] = useState("");
  const [checkingPhone, setCheckingPhone] = useState("");
  const [checkResult, setCheckResult] = useState(null);

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      window.location.href = "/login";
      return null;
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const fetchOptOuts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const params = new URLSearchParams({
        page: page.toString(),
        limit: "50",
        search: searchQuery,
        reason: reasonFilter,
        sortBy: "optedOutAt",
        sortOrder: "desc",
      });

      const res = await fetch(`${getApiBaseUrl()}/api/sms-optouts?${params}`, {
        headers,
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch opt-outs: ${res.status}`);
      }

      const data = await res.json();
      setOptOuts(data.optOuts);
      setTotalPages(data.pagination.pages);
      setTotal(data.pagination.total);
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, reasonFilter]);

  useEffect(() => {
    fetchOptOuts();
  }, [fetchOptOuts]);

  const handleAddOptOut = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${getApiBaseUrl()}/api/sms-optouts`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          phoneNumber: newPhoneNumber,
          reason: newReason,
          notes: newNotes,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to add opt-out");
      }

      setSuccess("Phone number added to opt-out list successfully");
      setNewPhoneNumber("");
      setNewReason("admin action");
      setNewNotes("");
      setShowAddModal(false);
      fetchOptOuts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveOptOut = async (phoneNumber) => {
    if (
      !window.confirm(
        `Remove ${phoneNumber} from opt-out list? They will start receiving SMS again.`,
      )
    ) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(
        `${getApiBaseUrl()}/api/sms-optouts/${encodeURIComponent(phoneNumber)}`,
        {
          method: "DELETE",
          headers,
        },
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to remove opt-out");
      }

      setSuccess(`${phoneNumber} removed from opt-out list`);
      fetchOptOuts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCheckPhone = async (e) => {
    e.preventDefault();
    setError("");
    setCheckResult(null);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(
        `${getApiBaseUrl()}/api/sms-optouts/check/${encodeURIComponent(checkingPhone)}`,
        { headers },
      );

      if (!res.ok) {
        throw new Error("Failed to check phone");
      }

      const data = await res.json();
      setCheckResult(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${getApiBaseUrl()}/api/sms-optouts/export`, {
        headers,
      });

      if (!res.ok) {
        throw new Error("Failed to export opt-outs");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sms-optouts-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess("Opt-out list exported successfully");
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const reasonColors = {
    "STOP keyword": "#ef4444",
    "user request": "#f59e0b",
    "admin action": "#6366f1",
    "invalid number": "#64748b",
    compliance: "#dc2626",
    other: "#71717a",
  };

  return (
    <PortalAdminLayout>
      <div style={{ padding: "32px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "32px",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: "700",
                color: "#111827",
                marginBottom: "8px",
              }}
            >
              📵 SMS Opt-Out Management
            </h1>
            <p style={{ color: "#6b7280", fontSize: "16px" }}>
              Manage customer SMS preferences and compliance
            </p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={handleExport}
              style={{
                padding: "12px 24px",
                backgroundColor: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              📥 Export CSV
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              style={{
                padding: "12px 24px",
                backgroundColor: "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              ➕ Add Opt-Out
            </button>
          </div>
        </div>

        {/* Alert Messages */}
        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              color: "#dc2626",
            }}
          >
            ❌ {error}
          </div>
        )}

        {success && (
          <div
            style={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              color: "#16a34a",
            }}
          >
            ✅ {success}
          </div>
        )}

        {/* Statistics Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "4px",
              }}
            >
              Total Opt-Outs
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#111827" }}
            >
              {total.toLocaleString()}
            </div>
          </div>

          {stats.reasonBreakdown &&
            Object.entries(stats.reasonBreakdown).map(([reason, count]) => (
              <div
                key={reason}
                style={{
                  backgroundColor: "white",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  {reason}
                </div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "700",
                    color: reasonColors[reason] || "#111827",
                  }}
                >
                  {count.toLocaleString()}
                </div>
              </div>
            ))}
        </div>

        {/* Check Phone Tool */}
        <div
          style={{
            backgroundColor: "#f9fafb",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "24px",
            border: "1px solid #e5e7eb",
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            🔍 Check Phone Status
          </h3>
          <form
            onSubmit={handleCheckPhone}
            style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}
          >
            <div style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Enter phone number (e.g., +17738959635)"
                value={checkingPhone}
                onChange={(e) => setCheckingPhone(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
            <button
              type="submit"
              style={{
                padding: "12px 24px",
                backgroundColor: "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Check
            </button>
          </form>

          {checkResult && (
            <div
              style={{
                marginTop: "16px",
                padding: "16px",
                backgroundColor: checkResult.isOptedOut ? "#fef2f2" : "#f0fdf4",
                borderRadius: "8px",
                border: `1px solid ${checkResult.isOptedOut ? "#fecaca" : "#bbf7d0"}`,
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                {checkResult.isOptedOut ? "🔴 Opted Out" : "🟢 Receiving SMS"}
              </div>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                Phone: {checkResult.phoneNumber}
              </div>
              {checkResult.optOut && (
                <>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    Since: {formatDate(checkResult.optOut.optedOutAt)}
                  </div>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    Reason: {checkResult.optOut.reason}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div
          style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "24px",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                🔎 Search Phone Number
              </label>
              <input
                type="text"
                placeholder="Search by phone number..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                📋 Filter by Reason
              </label>
              <select
                value={reasonFilter}
                onChange={(e) => {
                  setReasonFilter(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="all">All Reasons</option>
                <option value="STOP keyword">STOP Keyword</option>
                <option value="user request">User Request</option>
                <option value="admin action">Admin Action</option>
                <option value="invalid number">Invalid Number</option>
                <option value="compliance">Compliance</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Opt-Out List Table */}
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "60px", color: "#6b7280" }}
          >
            Loading opt-out list...
          </div>
        ) : optOuts.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px",
              backgroundColor: "white",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📱</div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              No Opt-Outs Found
            </div>
            <div style={{ color: "#6b7280" }}>
              {searchQuery || reasonFilter !== "all"
                ? "Try adjusting your filters"
                : "No customers have opted out of SMS"}
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
                overflow: "hidden",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f9fafb" }}>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Phone Number
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Opted Out Date
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Reason
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Source
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Notes
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "center",
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {optOuts.map((optOut) => (
                    <tr
                      key={optOut._id}
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      <td
                        style={{
                          padding: "16px",
                          fontFamily: "monospace",
                          fontSize: "14px",
                        }}
                      >
                        {optOut.phoneNumber}
                      </td>
                      <td style={{ padding: "16px", fontSize: "14px" }}>
                        {formatDate(optOut.optedOutAt)}
                      </td>
                      <td style={{ padding: "16px" }}>
                        <span
                          style={{
                            padding: "4px 12px",
                            borderRadius: "12px",
                            backgroundColor:
                              reasonColors[optOut.reason] + "20" || "#f3f4f6",
                            color: reasonColors[optOut.reason] || "#111827",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          {optOut.reason}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          fontSize: "14px",
                          color: "#6b7280",
                        }}
                      >
                        {optOut.metadata?.source || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "16px",
                          fontSize: "14px",
                          color: "#6b7280",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {optOut.metadata?.notes || "-"}
                      </td>
                      <td style={{ padding: "16px", textAlign: "center" }}>
                        <button
                          onClick={() => handleRemoveOptOut(optOut.phoneNumber)}
                          style={{
                            padding: "6px 16px",
                            backgroundColor: "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          Re-enable SMS
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "12px",
                  marginTop: "24px",
                }}
              >
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: page === 1 ? "#f3f4f6" : "#6366f1",
                    color: page === 1 ? "#9ca3af" : "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: page === 1 ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  ← Previous
                </button>
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  style={{
                    padding: "8px 16px",
                    backgroundColor:
                      page === totalPages ? "#f3f4f6" : "#6366f1",
                    color: page === totalPages ? "#9ca3af" : "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                    fontWeight: "600",
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        {/* Add Opt-Out Modal */}
        {showAddModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowAddModal(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "32px",
                borderRadius: "12px",
                maxWidth: "500px",
                width: "90%",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  marginBottom: "24px",
                }}
              >
                Add Phone to Opt-Out List
              </h2>

              <form onSubmit={handleAddOptOut}>
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="+17738959635"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Reason *
                  </label>
                  <select
                    value={newReason}
                    onChange={(e) => setNewReason(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="admin action">Admin Action</option>
                    <option value="user request">User Request</option>
                    <option value="compliance">Compliance</option>
                    <option value="invalid number">Invalid Number</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Notes (Optional)
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Add any additional context..."
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    style={{
                      padding: "12px 24px",
                      backgroundColor: "#f3f4f6",
                      color: "#374151",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: "12px 24px",
                      backgroundColor: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Add to Opt-Out List
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default SMSOptOutManagement;
